/**
 * Debug-log context shared by `DebugLogProvider`, the Developer Dock
 * and its launchers. Lives apart from the provider so the hooks are
 * plain module exports (react-refresh wants component files to export
 * components only).
 */
import { createContext, useContext } from 'react';

export type LogStatus = 'pending' | 'ok' | 'error';

export interface DebugLogEntry {
    /** Stable id used as React key + for the dock's expand-row state. */
    id: string;
    /** Epoch ms when the request was initiated (operator's local clock). */
    timestamp: number;
    method: string;
    url: string;
    /** Headers with the Authorization value scrubbed. */
    requestHeaders: Record<string, string>;
    requestBody: unknown;
    /** "pending" until the response interceptor lands; then "ok" / "error". */
    status: LogStatus;
    /** HTTP status code once we have one (null while pending). */
    statusCode: number | null;
    responseBody: unknown;
    /** Ms the round-trip took, populated once finished. */
    durationMs: number | null;
    /** Error message when status === "error". */
    errorMessage: string | null;
}

export interface DebugLogContextValue {
    entries: DebugLogEntry[];
    paused: boolean;
    enabled: boolean;
    /** Whether the Developer Dock drawer is open — shared so the admin
     *  header's launcher and the floating one drive the same drawer. */
    dockOpen: boolean;
    setDockOpen: (open: boolean) => void;
    togglePause: () => void;
    clear: () => void;
}

export const DebugLogContext = createContext<DebugLogContextValue | undefined>(undefined);

/**
 * The debug log when a provider is mounted, `undefined` outside one —
 * for chrome that renders both inside and outside the dev tooling.
 */
export function useDebugLogOptional(): DebugLogContextValue | undefined {
    return useContext(DebugLogContext);
}

export function useDebugLog(): DebugLogContextValue {
    const ctx = useDebugLogOptional();
    if (!ctx) {
        throw new Error('useDebugLog must be used within a DebugLogProvider');
    }
    return ctx;
}
